import AppIcon from '../components/AppIcon';
import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../hooks/useAuth';
import { GET_MEMBERS } from '../services/members';

function DashboardPage() {
  const { user } = useAuth();
  const [personId, setPersonId] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('mien_person_id');
    if (stored) setPersonId(stored);
    else if (user?.personId) {
      setPersonId(String(user.personId));
      localStorage.setItem('mien_person_id', String(user.personId));
    }
  }, [user]);

  const { data: membersData } = useQuery(GET_MEMBERS, {
    variables: { offset: 0, limit: 1 },
  });

  const memberCount = membersData?.members?.total || 0;

  const stats = [
    { label: 'Family Members', value: memberCount > 0 ? String(memberCount) : '—', icon: 'users', color: 'bg-blue-50 text-blue-700', href: '/members' },
    { label: 'Memories', value: '—', icon: 'image', color: 'bg-green-50 text-green-700', href: '/memories' },
    { label: 'Invitations', value: '—', icon: 'mail', color: 'bg-purple-50 text-purple-700', href: '/invitations' },
    { label: 'Kinship Terms', value: '38', icon: 'book', color: 'bg-amber-50 text-amber-700', href: '/kinship' },
  ];

  const quickActions = [
    { title: 'Family Tree', desc: 'View and explore your family tree', icon: 'family-tree', href: '/family-tree' },
    { title: 'Add Memory', desc: 'Share a photo or story', icon: 'camera', href: '/memories/new' },
    { title: 'Invite Family', desc: 'Bring relatives into the family', icon: 'mail', href: '/invitations/new' },
    { title: 'Members', desc: 'Manage family members', icon: 'overview', href: '/members' },
    { title: 'Cultural Terms', desc: 'Explore Mien cultural heritage', icon: 'cultural', href: '/cultural-terms' },
    { title: 'Settings', desc: 'Manage your account', icon: 'settings', href: '/settings' },
  ];

  // Show profile setup prompt for new users without a person record
  const needsProfile = !personId;

  const recentActivity = needsProfile
    ? [
        { text: 'Welcome to Mien Family! Complete your profile to get started.', time: 'Just now', icon: 'welcome' },
      ]
    : [
        { text: 'Welcome to Mien Family! Start by adding your first memory or inviting relatives.', time: 'Just now', icon: 'welcome' },
      ];

  const upcomingEvents = [
    { name: 'No upcoming events yet', date: '', type: '' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Welcome{user?.email ? `, ${user.email.split('@')[0]}` : ''} 👋</h2>
        <p className="mt-1 text-gray-600">Here's an overview of your family hub.</p>
      </div>

      {/* Profile Setup Banner */}
      {needsProfile && (
        <a href="/settings" className="block mb-6 bg-indigo-50 border border-indigo-200 rounded-xl p-5 hover:bg-indigo-100 transition-colors">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <AppIcon name="overview" size={24} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-indigo-800">Complete your profile</h3>
              <p className="text-sm text-indigo-600 mt-1">Add your personal details to start building your family tree.</p>
            </div>
            <div className="ml-auto text-indigo-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </div>
          </div>
        </a>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <a
            key={stat.label}
            href={stat.href}
            className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md hover:border-indigo-200 transition-all"
          >
            <div className="flex items-center gap-3">
              <AppIcon name={stat.icon} size={24} />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color.split(' ')[1]}`}>{stat.value}</p>
              </div>
            </div>
          </a>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {quickActions.map((action) => (
              <a
                key={action.title}
                href={action.href}
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md hover:border-indigo-200 transition-all group"
              >
                <AppIcon name={action.icon} size={30} />
                <h4 className="mt-3 text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                  {action.title}
                </h4>
                <p className="mt-1 text-xs text-gray-500">{action.desc}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-50">
              {recentActivity.map((item, i) => (
                <div key={i} className="p-4 flex items-start gap-3">
                  <AppIcon name={item.icon} size={18} />
                  <div>
                    <p className="text-sm text-gray-700">{item.text}</p>
                    <p className="text-xs text-gray-400 mt-1">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              {upcomingEvents.map((event, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <AppIcon name="cake" size={18} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-700">{event.name}</p>
                    {event.date && <p className="text-xs text-gray-400">{event.date}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;